import { LoadingIndicator } from '@leagueplatform/web-common-components';
import { BasePage } from 'components/base-page/base-page.component';
import { HealthAssessmentPrompt } from 'components/health-assessment-prompt/health-assessment-prompt.component';
import { OnboardingFeatureHighlights } from 'components/onboarding-feature-highlights/onboarding-feature-highlights.component';
import { useHealthAssessmentPrompt } from 'hooks/use-health-assessment-prompt.hook';
import { useOnboarding } from 'hooks/use-onboarding.hook';
import {
  useCaregiverStatus,
  useIsCaregiverShell,
} from 'hooks/use-is-caregiver.hook';
import { CaregiverStatusError } from 'components/caregiver-status-error/caregiver-status-error.component';

export const PrivateRoutesContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { onOnboardingComplete, shouldShowFeatureHighlights, isLoading } =
    useOnboarding();

  const {
    shouldShowHealthAssessmentPrompt,
    onOnboardingComplete: onShowHealthAssessmentPrompt,
    onStartHealthAssessment,
    onDismissHealthAssessmentPrompt,
  } = useHealthAssessmentPrompt({
    isOnboardingReady: !isLoading,
    shouldShowFeatureHighlights,
  });

  const handleOnboardingComplete = (error?: Error) => {
    onOnboardingComplete(error);
    if (!error) {
      onShowHealthAssessmentPrompt();
    }
  };

  // Member onboarding + health-assessment gates run above the routed content,
  // so they must not fire for a non-impersonating caregiver — otherwise a
  // caregiver-at-rest on a fresh browser is pulled into the member onboarding
  // flow before the route guard can redirect them to the caregiver home.
  // Wait for the caregiver claim to resolve, then skip these gates for the
  // caregiver shell; the route guards handle their routing.
  const status = useCaregiverStatus();
  const isCaregiverShell = useIsCaregiverShell();

  // Unknown caregiver status must not fall through to the member gates below:
  // running member onboarding for a caregiver is precisely the wrong guess.
  if (status === 'error') {
    return <CaregiverStatusError />;
  }

  if (isLoading || isCaregiverShell === null) {
    return <LoadingIndicator />;
  }

  if (!isCaregiverShell) {
    if (shouldShowFeatureHighlights) {
      return (
        <OnboardingFeatureHighlights
          onOnboardingComplete={handleOnboardingComplete}
        />
      );
    }

    if (shouldShowHealthAssessmentPrompt) {
      return (
        <HealthAssessmentPrompt
          onStartHealthAssessment={onStartHealthAssessment}
          onDismiss={onDismissHealthAssessmentPrompt}
        />
      );
    }
  }

  return <BasePage>{children}</BasePage>;
};
